import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import PaymentsTabs from '../../../components/admin/PaymentsTabs';
import axios from 'axios';
import {
  Wallet,
  Loader2,
  ArrowDownToLine,
  ArrowUpFromLine,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Globe,
  Coins,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONN_STATUS = {
  connected: { text: 'Connected', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  invalid_token: { text: 'Invalid Token', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  not_configured: { text: 'Not Configured', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  api_error: { text: 'API Error', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  network_error: { text: 'Network Error', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  timeout: { text: 'Timeout', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

const PaymentsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [conn, setConn] = useState(null);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, cn] = await Promise.all([
        axios.get(`${API_URL}/api/admin/payments/overview`),
        axios.get(`${API_URL}/api/admin/payments/connection`),
      ]);
      setOverview(ov.data);
      setConn(cn.data);
      // Only fetch balances if the connection works
      if (cn.data.status === 'connected') {
        try {
          const wb = await axios.get(`${API_URL}/api/admin/payments/wallet-balances`);
          setBalances(wb.data);
        } catch (e) { setBalances(null); }
      } else {
        setBalances(null);
      }
    } catch (e) {
      console.error('Error loading payments dashboard:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const KpiCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-xs text-slate-400">{label}</p>
        </div>
      </div>
    </div>
  );

  const connMeta = CONN_STATUS[conn?.status] || CONN_STATUS.not_configured;
  const isProd = conn?.environment === 'production';

  // Available currencies: union of every currency the live API actually returned
  // (active-conf currencies, per-country currency, and wallet-balance currencies).
  // Never hardcoded — derived purely from the real responses.
  const availableCurrencies = useMemo(() => {
    const set = new Set();
    (conn?.currencies || []).forEach((c) => c && set.add(c));
    (conn?.countries || []).forEach((c) => c?.currency && set.add(c.currency));
    (balances?.balances || []).forEach((b) => b?.currency && set.add(b.currency));
    return Array.from(set).sort();
  }, [conn, balances]);

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" data-testid="payments-dashboard-title">
          <Wallet className="w-6 h-6 text-amber-400" />
          Payments
        </h1>
        <p className="text-slate-400">PawaPay mobile-money operations overview.</p>
      </div>

      <PaymentsTabs />

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
      ) : (
        <>
          {/* Status banner */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span
              data-testid="payments-environment-badge"
              className={`px-3 py-1.5 text-xs font-medium rounded border ${
                isProd ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
              }`}
            >
              {isProd ? 'PRODUCTION' : 'SANDBOX'}
            </span>
            <span data-testid="payments-connection-status" className={`px-3 py-1.5 text-xs font-medium rounded border ${connMeta.color}`}>
              {connMeta.text}
            </span>
            {conn?.company_name && (
              <span className="text-sm text-slate-400">Account: {conn.company_name}</span>
            )}
          </div>

          {/* Connection message if not connected */}
          {conn?.status !== 'connected' && (
            <div className="flex items-start gap-3 p-4 mb-6 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-300">
              <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">
                {conn?.message || 'PawaPay is not connected.'} Configure the API token in the <strong>Settings</strong> tab.
              </span>
            </div>
          )}

          {/* Countries / Currencies (from active-conf) */}
          {conn?.status === 'connected' && (
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3 text-white font-semibold">
                  <Globe className="w-5 h-5 text-amber-400" /> Available Countries
                </div>
                <div className="flex flex-wrap gap-2" data-testid="available-countries">
                  {conn.countries.length === 0 ? (
                    <span className="text-slate-500 text-sm">None</span>
                  ) : conn.countries.map((c) => (
                    <span key={c.country} className="px-2.5 py-1 text-xs rounded border border-slate-600 text-slate-300" title={`${c.providers.length} providers`}>
                      {c.country} · {c.currency}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3 text-white font-semibold">
                  <Coins className="w-5 h-5 text-amber-400" /> Available Currencies
                </div>
                <div className="flex flex-wrap gap-2" data-testid="available-currencies">
                  {availableCurrencies.length === 0 ? (
                    <span className="text-slate-500 text-sm">None</span>
                  ) : availableCurrencies.map((cur) => (
                    <span key={cur} className="px-2.5 py-1 text-xs rounded border border-slate-600 text-slate-300">{cur}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Wallet balances */}
          {conn?.status === 'connected' && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden mb-6">
              <div className="px-5 py-3 border-b border-slate-700 text-white font-semibold flex items-center gap-2">
                <Wallet className="w-5 h-5 text-amber-400" /> Wallet Balances
              </div>
              {!balances || balances.balances.length === 0 ? (
                <div className="px-5 py-6 text-slate-500 text-sm" data-testid="wallet-empty">
                  {balances?.message || 'No wallet balances returned.'}
                </div>
              ) : (
                <table className="w-full" data-testid="wallet-balances-table">
                  <thead>
                    <tr className="border-b border-slate-700">
                      {['Country', 'Currency', 'Provider', 'Balance'].map((h) => (
                        <th key={h} className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-5 py-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50">
                    {balances.balances.map((b, i) => (
                      <tr key={i} className="hover:bg-slate-700/20">
                        <td className="px-5 py-3 text-sm text-slate-300">{b.country || '-'}</td>
                        <td className="px-5 py-3 text-sm text-slate-400">{b.currency || '-'}</td>
                        <td className="px-5 py-3 text-sm text-slate-400">{b.provider || 'System'}</td>
                        <td className="px-5 py-3 text-sm text-white font-mono">{b.balance ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KpiCard icon={ArrowDownToLine} label="Deposits" value={overview?.deposits ?? 0} color="bg-emerald-500/20 text-emerald-400" />
            <KpiCard icon={ArrowUpFromLine} label="Payouts" value={overview?.payouts ?? 0} color="bg-cyan-500/20 text-cyan-400" />
            <KpiCard icon={Clock} label="Pending" value={overview?.pending ?? 0} color="bg-amber-500/20 text-amber-400" />
            <KpiCard icon={CheckCircle2} label="Completed" value={overview?.completed ?? 0} color="bg-emerald-500/20 text-emerald-400" />
            <KpiCard icon={XCircle} label="Failed" value={overview?.failed ?? 0} color="bg-red-500/20 text-red-400" />
            <KpiCard icon={AlertTriangle} label="Total" value={overview?.total ?? 0} color="bg-slate-500/20 text-slate-400" />
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default PaymentsDashboard;
